import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import _ from "lodash"

import { IFilter, IFilterState, ISegment } from "./types"
import { safeLocalStorage } from "../../../../../../../helpers/utils/safeStorage"
import { REPORT_FILTER_CONFIG, ReportFilterType } from "../../../../../../../shared/common/components/reports/params/filter/types"

const savedSegment = safeLocalStorage.getItem("current_segment")

const DASHBOARD_REPORT_TYPES: ReportFilterType[] = [
  "universal",
  "ad",
  "order",
  "payment",
  "webinar",
  "visit",
]

function buildDashboardCommonByReportType(filters: IFilter[]): Record<string, IFilter[]> {
  const result: Record<string, IFilter[]> = {}

  DASHBOARD_REPORT_TYPES.forEach((rt) => {
    const categories = REPORT_FILTER_CONFIG?.[rt]?.categories ?? []
    if (!categories.length) {
      result[rt] = filters
      return
    }

    result[rt] = filters.filter((f: any) => {
      const rootCategory =
        Array.isArray(f?.namePath) && typeof f.namePath[0] === "string"
          ? f.namePath[0]
          : null

      // Совместимость: если нельзя определить категорию — не режем.
      if (!rootCategory) return true
      return categories.includes(rootCategory)
    })
  })

  return result
}

const defaultState = {
  filters: [],
  dashboardCommonByReportType: {},
  filterSelectorVisible: false,
  currentSegment: savedSegment ? JSON.parse(savedSegment) : null,
  segments: [],
  funnels: [],
  segmentsLoading: false,
  filterParent: [],
  currentFunnel: [],
  blocks: {},
  tgUsers: [],
  yandexMetric: {
    currentSegment: null,
    filters: [],
    currentFunnel: [],
    filterParent: [],
  },
  dashboardModal: {
    currentSegment: null,
    filters: [],
    currentFunnel: [],
    filterParent: [],
  },
  // Фильтры для модального окна редактирования кастомной метрики
  metricModal: {
    filters: [],
    filterParent: [],
  },
}
const getInitialState = () => {
  return defaultState
}
const initialState: IFilterState = getInitialState()

export const filterSlice = createSlice({
  name: "filter",
  initialState: initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<IFilter[] | any>) => {
      let filters: any[] = []
      if (Array.isArray(action.payload)) {
        filters = _.cloneDeep(action.payload)
      } else {
        filters = _.cloneDeep(action.payload.data)
      }
      // Важно: не мутируем данные фильтров (нормализация делается при формировании payload).

      // Если payload - простой массив, записываем в глобальные фильтры и выходим
      if (Array.isArray(action.payload)) {
        state.filters = filters
        state.dashboardCommonByReportType = buildDashboardCommonByReportType(filters)
        return
      }

      // Проверяем флаги для определения целевого хранилища фильтров
      if (action.payload?.isMetricModal) {
        // Фильтры для модального окна редактирования метрики
        state.metricModal.filters = filters
      } else if (action.payload?.isModal) {
        // Фильтры для модального окна дашборда
        state.dashboardModal.filters = filters
      } else if (action.payload?.isYM) {
        // Фильтры для задач Яндекс.Метрики
        state.yandexMetric.filters = filters
      } else if (action.payload?.blockId) {
        // Фильтры для конкретного блока
        if (!state.blocks[action.payload.blockId]) {
          state.blocks[action.payload.blockId] = { filters: [] }
        }
        state.blocks[action.payload.blockId].filters = filters
      } else {
        // Глобальные фильтры по умолчанию
        state.filters = filters
        state.dashboardCommonByReportType = buildDashboardCommonByReportType(filters)
      }
    },

    setFilter: (
      state,
      action: PayloadAction<IFilter & { isModal?: boolean; isMetricModal?: boolean; isYM?: boolean; blockId?: string }>
    ) => {
      let _filters = _.cloneDeep(
        action.payload.isMetricModal
          ? state.metricModal.filters
          : action.payload.isModal
            ? state.dashboardModal.filters
            : action.payload.isYM
              ? state.yandexMetric.filters
              : state.filters
      )

      const filter = JSON.parse(JSON.stringify(action.payload))

      _filters.push(filter)
      if (action.payload.isMetricModal) {
        state.metricModal.filters = _filters
        return
      }
      if (action.payload.isModal) {
        state.dashboardModal.filters = _filters
        return
      }
      if (action.payload.isYM) {
        state.yandexMetric.filters = _filters
        return
      }
      if (action.payload.blockId) {
        if (!state.blocks[action.payload.blockId]) {
          state.blocks[action.payload.blockId] = { filters: [] }
        }
        state.blocks[action.payload.blockId].filters = _filters
        return
      }
      state.filters = _filters
      state.dashboardCommonByReportType = buildDashboardCommonByReportType(_filters)
    },

    setFilterParent: (state, action: PayloadAction<any>) => {
      if (
        action.payload &&
        typeof action.payload === "object" &&
        "data" in action.payload &&
        Array.isArray(action.payload.data)
      ) {
        if (action.payload.isMetricModal) {
          state.metricModal.filterParent = []
        } else if (action.payload.isModal) {
          state.dashboardModal.filterParent = []
        } else if (action.payload.isYM) {
          state.yandexMetric.filterParent = []
        } else {
          state.filterParent = []
        }
        return
      }

      // Если payload - обычный массив (legacy поддержка)
      if (Array.isArray(action.payload)) {
        state.filterParent = []
        return
      }

      // Если payload - объект с id и name, ищем элемент и добавляем его
      if (
        action.payload &&
        typeof action.payload === "object" &&
        "id" in action.payload &&
        "name" in action.payload
      ) {
        // Определяем целевой filterParent
        const targetParent = action.payload.isMetricModal
          ? state.metricModal.filterParent
          : action.payload.isModal
            ? state.dashboardModal.filterParent
            : action.payload.isYM
              ? state.yandexMetric.filterParent
              : state.filterParent

        const findElement = targetParent.find(
          (el) => el.id === action.payload.id
        )

        if (findElement) {
          return // Элемент уже существует, ничего не делаем
        }

        // Добавляем новый элемент в соответствующий filterParent
        if (action.payload.isMetricModal) {
          state.metricModal.filterParent = [
            ...state.metricModal.filterParent,
            { id: action.payload.id, name: action.payload.name },
          ]
        } else if (action.payload.isModal) {
          state.dashboardModal.filterParent = [
            ...state.dashboardModal.filterParent,
            { id: action.payload.id, name: action.payload.name },
          ]
        } else if (action.payload.isYM) {
          state.yandexMetric.filterParent = [
            ...state.yandexMetric.filterParent,
            { id: action.payload.id, name: action.payload.name },
          ]
        } else {
          state.filterParent = [
            ...state.filterParent,
            { id: action.payload.id, name: action.payload.name },
          ]
        }
      }
    },
    setFilterSelectorVisible: (state, action: PayloadAction<boolean>) => {
      state.filterSelectorVisible = action.payload
    },
    setSegments: (state, action: PayloadAction<ISegment[]>) => {
      state.segments = action.payload
    },
    setCurrentSegment: (
      state,
      action: PayloadAction<(ISegment & { isModal?: boolean }) | null>
    ) => {
      action.payload?.isModal
        ? state.dashboardModal.currentSegment
        : (state.currentSegment = action.payload)
    },
    setCurrentFunnel: (
      state,
      action: PayloadAction<
        ISegment[] | { data: ISegment[]; isModal?: boolean } | null
      >
    ) => {
      if (Array.isArray(action.payload)) {
        state.currentFunnel = action.payload
      } else {
        action.payload?.isModal
          ? state.dashboardModal.currentFunnel
          : (state.currentFunnel = action.payload?.data ?? null)
      }
    },
    setFunnels: (state, action: PayloadAction<ISegment[]>) => {
      state.funnels = action.payload
    },
    setSegmentsLoading: (state, action: PayloadAction<boolean>) => {
      state.segmentsLoading = action.payload
    },
    setTGUsers: (state, action: PayloadAction<any[]>) => {
      state.tgUsers = action.payload
    },
    /**
     * Очищает состояние фильтров для конкретного блока дашборда.
     * Вызывается при удалении блока для предотвращения утечки памяти.
     */
    clearBlockFilters: (state, action: PayloadAction<string>) => {
      const blockId = action.payload
      if (state.blocks && state.blocks[blockId]) {
        delete state.blocks[blockId]
      }
    },
  },
})

export const filterSliceReducer = filterSlice.reducer
export const filterSliceActions = filterSlice.actions
